import PropTypes from 'prop-types';
import { useCallback, forwardRef } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import CustomizableMenuV2 from 'hb-react/frontoffice/components/MenuV2';
import MobileMenu from 'hb-react/frontoffice/components/MenuV2/MobileMenu';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { dynamicTopicResourcePath } from 'hb-react/shared/apps/Topics/utils';
import useGroupNavigationTheme from 'hb-react/shared/hooks/theme/useGroupNavigationTheme';
import ThemeProvider from 'hb-react/style/ThemeProvider';

const Menu = ({ ariaLabel, items = [], isLoading = false, menuRef }) => {
  const groupNavigationTheme = useGroupNavigationTheme();
  const breakpoint = useBreakpoint();
  const isMobileDevice = useIsMobileDevice();
  const { whitelabelURL, topicId } = useTopicContext();
  const rootPath = whitelabelURL ? '/' : Routes.topic_path(topicId);

  const handleGetPageUrl = useCallback(
    (path) =>
      dynamicTopicResourcePath(path, { whitelabelURL, topicId }, (pathRoute) =>
        path === pathRoute ? '/' : '',
      ),
    [whitelabelURL, topicId],
  );
  const handleItemClick = useCallback((e) => {
    e?.target?.blur();
  }, []);

  return breakpoint === 's' || isMobileDevice ? (
    <Box alignSelf="flex-end" flex={1}>
      <ThemeProvider theme={groupNavigationTheme.themeName}>
        <MobileMenu
          dynamic
          ariaLabel={ariaLabel}
          getPageUrl={handleGetPageUrl}
          isLoading={isLoading}
          items={items}
          rootPath={rootPath}
        />
      </ThemeProvider>
    </Box>
  ) : (
    <div ref={menuRef} data-testid="topic-menu">
      <CustomizableMenuV2
        ariaLabel={ariaLabel}
        dynamic
        getPageUrl={handleGetPageUrl}
        isLoading={isLoading}
        items={items}
        rootPath={rootPath}
        onItemClick={handleItemClick}
      />
    </div>
  );
};

Menu.propTypes = {
  ariaLabel: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
};

export default forwardRef((props, ref) => <Menu {...props} menuRef={ref} />);
